/**
 * liveCallsContext — ONE EventSource for the whole console, mounted at the shell
 * level so it survives tab switches (fixes: a live call vanished when you left
 * Live Calls and came back, because the old per-screen hook unmounted its stream
 * and SSE only pushes NEW frames, never current state).
 *
 * Subscribes to GET /v1/live/calls (org-scoped, httpOnly-cookie auth). Accumulates:
 *   - the set of currently-active calls (status frames), and
 *   - a per-call live transcript (call.turn.caller / call.turn.assistant frames).
 *
 * No mock data — an empty feed means no live calls.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Voice_Live_Calls_Url } from '../api/endpoints';

// The compact frame the backend publishes (orchestrator liveEvents.LiveCallFrame).
interface LiveCallFrame {
  eventType: string;
  callId?: string;
  callSid?: string;
  callerPhone?: string;
  status?: string;
  locationId?: string;
  direction?: 'inbound' | 'outbound'; // present on call.started
  text?: string; // present on call.turn.* frames
  seq?: number;  // monotonic speech-start order (call.turn.* frames)
  speaker?: 'caller' | 'agent'; // present on call.turn.partial frames
  isFinal?: boolean;            // false on call.turn.partial frames
  intent?: string;              // detected intent, when the runtime tags it
  at: string;
}

// `partial` = still-forming interim line (rendered dimmed); replaced in place by
// the final call.turn.* frame carrying the same seq.
export interface LiveTranscriptLine { sp: 'caller' | 'agent'; text: string; at: number; seq: number; partial?: boolean }

export interface LiveCall {
  id: string;
  num: string;
  status: string;
  dir?: 'inbound' | 'outbound'; // set on call.started; persisted across later frames
  startedAt: number; // ms epoch of first frame, for the elapsed timer
  intent?: string;   // latest detected intent for this call (chip source)
}

// ONE line per active caller (not a per-turn log): each caller occupies a single
// row showing its LATEST utterance, which updates in place as the call proceeds —
// the "each turn morphs the same line" behaviour. num + intent tag the row.
export interface CallerFeedLine {
  callId: string;
  num: string;
  intent?: string;
  sp: 'caller' | 'agent';
  text: string;
  at: number;
  partial?: boolean;
}

// A completed-action item for the bottom ticker (label + when).
export interface ActionTickerItem { id: string; label: string; at: number; }

interface LiveCallsValue {
  calls: LiveCall[];
  connected: boolean;
  /** Live transcript lines for a given call id (empty if none yet). */
  transcriptFor: (callId: string | null) => LiveTranscriptLine[];
  /** One row per active caller — its latest turn, updating live. Newest call first. */
  callerFeed: CallerFeedLine[];
  /** Rolling list of completed actions (newest first) for the bottom ticker. */
  actionTicker: ActionTickerItem[];
}

const LiveCallsContext = createContext<LiveCallsValue>({
  calls: [], connected: false, transcriptFor: () => [], callerFeed: [], actionTicker: [],
});

// Event types that arrive as ticker items (label carried in the frame's `text`).
const ACTION_EVENTS = /^(reservation|appointment|order|bill|waitlist|ticket)\./;

// Terminal statuses/events that remove a call from the active set.
const ENDED = /(END|COMPLETE|HANGUP|FAIL|DROP|ABANDON)/i;

export function LiveCallsProvider({ children }: { children: React.ReactNode }) {
  const [calls, setCalls] = useState<LiveCall[]>([]);
  const [connected, setConnected] = useState(false);
  // Transcript kept in state (keyed by callId) so consumers re-render on new lines.
  const [transcripts, setTranscripts] = useState<Record<string, LiveTranscriptLine[]>>({});
  const [actionTicker, setActionTicker] = useState<ActionTickerItem[]>([]);
  const active = useRef<Map<string, LiveCall>>(new Map());

  useEffect(() => {
    if (typeof window === 'undefined' || typeof (window as any).EventSource === 'undefined') return;
    const es = new (window as any).EventSource(Voice_Live_Calls_Url, { withCredentials: true });

    const commit = () => setCalls(Array.from(active.current.values()));

    es.addEventListener('ready', () => setConnected(true));
    es.addEventListener('call', (ev: MessageEvent) => {
      let frame: LiveCallFrame;
      try { frame = JSON.parse(ev.data); } catch { return; }

      // Completed-action frame (no callId) → bottom ticker. Label is in `text`.
      if (ACTION_EVENTS.test(frame.eventType || '')) {
        const label = frame.text || frame.eventType;
        setActionTicker(prev => [{ id: frame.eventType + ':' + frame.at + ':' + prev.length, label, at: Date.parse(frame.at) || Date.now() }, ...prev].slice(0, 24));
        return;
      }

      const id = frame.callId || frame.callSid;
      if (!id) return;

      // Transcript turn (final OR interim). Both carry the same seq for an
      // utterance, so we UPSERT by (speaker, seq): an interim frame creates/updates
      // a dimmed in-progress line; the final frame replaces it in place. This is the
      // industry-standard interim+final pattern — live words, clean final sentence.
      const isTurn =
        frame.eventType === 'call.turn.caller' ||
        frame.eventType === 'call.turn.assistant' ||
        frame.eventType === 'call.turn.partial';
      if (isTurn) {
        const sp: LiveTranscriptLine['sp'] =
          frame.eventType === 'call.turn.caller' ? 'caller'
          : frame.eventType === 'call.turn.assistant' ? 'agent'
          : (frame.speaker ?? 'caller'); // partial carries speaker explicitly
        const partial = frame.eventType === 'call.turn.partial';
        if (frame.text && frame.text.trim()) {
          const line: LiveTranscriptLine = {
            sp, text: frame.text, at: Date.parse(frame.at) || Date.now(),
            // Fall back to arrival order when seq is absent (older frames).
            seq: typeof frame.seq === 'number' ? frame.seq : Number.MAX_SAFE_INTEGER,
            partial,
          };
          setTranscripts(prev => {
            const lines = prev[id] ?? [];
            // Upsert by (sp, seq): update the in-progress line for this utterance,
            // don't append a new one per interim frame.
            const idx = lines.findIndex(l => l.sp === sp && l.seq === line.seq);
            const next = idx >= 0
              ? lines.map((l, i) => (i === idx ? line : l))
              : [...lines, line];
            // Order by speech-start seq so the reply never sorts above the greeting.
            next.sort((a, b) => a.seq - b.seq);
            return { ...prev, [id]: next };
          });
        }
        // A turn also proves the call is live — keep it in the active set below.
      }

      const ended = ENDED.test(frame.eventType || '') || ENDED.test(frame.status || '');
      if (ended) {
        active.current.delete(id);
      } else {
        const prev = active.current.get(id);
        active.current.set(id, {
          id,
          num: frame.callerPhone || prev?.num || 'unknown',
          status: frame.status || prev?.status || 'live',
          dir: frame.direction ?? prev?.dir, // arrives on call.started; keep it after
          startedAt: prev?.startedAt ?? (Date.parse(frame.at) || Date.now()),
          // Latest detected intent for this call (drives the feed's intent chip).
          intent: frame.intent || prev?.intent,
        });
      }
      commit();
    });
    es.onerror = () => setConnected(false);

    return () => { es.close(); active.current.clear(); };
  }, []);

  const transcriptFor = useCallback(
    (callId: string | null) => (callId ? transcripts[callId] ?? [] : []),
    [transcripts],
  );

  // Scrolling log: EVERY turn from all active callers, newest first — the feed
  // keeps growing as the conversations go on (not collapsed per caller). Each
  // line tagged with its call's masked number + intent.
  const callerFeed = useMemo<CallerFeedLine[]>(() => {
    const byId = new Map(calls.map(c => [c.id, c]));
    const lines: CallerFeedLine[] = [];
    for (const [callId, tlines] of Object.entries(transcripts)) {
      const call = byId.get(callId);
      if (!call) continue; // drop turns of calls no longer active
      for (const l of tlines) {
        lines.push({ callId, num: call.num, intent: call.intent, sp: l.sp, text: l.text, at: l.at, partial: l.partial });
      }
    }
    return lines.sort((a, b) => b.at - a.at); // newest turn first
  }, [transcripts, calls]);

  return (
    <LiveCallsContext.Provider value={{ calls, connected, transcriptFor, callerFeed, actionTicker }}>
      {children}
    </LiveCallsContext.Provider>
  );
}

export const useLiveCalls = (): LiveCallsValue => useContext(LiveCallsContext);
